import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../../utils/cms.utils";
import ContentTag from "../../../utils/tag";
import {
  getAgeRangePublicRoute,
  isAgeRangeAllowed,
} from "../../../utils/ageRange.util";
import { useInputMode } from "../../../utils/hooks/useInputMode";
import OptimizedImage from "../../common/OptimizedImage";

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

// ============================================================================
// COMPONENTS
// ============================================================================

const MovieItem = ({ item = {}, onEnterPress, onFocus }) => {
  const { contentPhotos, slug, tags } = item;
  const { focused, ref, focusSelf } = useFocusable({
    onEnterPress,
    onFocus,
    focusKey: "content_" + slug,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <div className="legacy-anavatan-grid-item-wrap">
      <div className="legacy-anavatan-grid-item-shell">
        <div
          ref={ref}
          onMouseEnter={handleMouseEnter}
          onClick={onEnterPress}
          className={"legacy-anavatan-grid-card" + (focused ? " is-focused" : "")}
        >
          <OptimizedImage
            src={imageData.src}
            alt={item.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit',
            }}
          />
          {tags?.[0] && <ContentTag label={tags?.[0]} />}
          <div className="legacy-anavatan-grid-overlay">
            <span className="legacy-anavatan-grid-card-title">{item.title}</span>
            <span className="legacy-anavatan-grid-card-year">{item.year}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Single row of movie items with its own focus context
 */
const MovieRow = ({ items, onFocus, navigate }) => {
  const { ref, focusKey } = useFocusable({ onFocus });

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-anavatan-grid-row" ref={ref}>
        {items.map((item, index) => (
          <MovieItem
            item={item}
            key={index}
            onFocus={onFocus}
            onEnterPress={() =>
              navigate(
                `/${item.contentType?.kind || item.kind || "movie"}/${item.slug}`
              )
            }
          />
        ))}
      </div>
    </FocusContext.Provider>
  );
};

export const MoviesSlider = ({ movies, onFocus }) => {
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter((x) => isAgeRangeAllowed(x.ageRange, ageRange));
  const { ref, focusKey } = useFocusable();

  const navigate = useNavigate();

  // Chunk items into rows
  const chunkedItems = useMemo(
    function () {
      if (!_items || _items.length === 0) return [];
      return chunkArray(_items, ITEMS_PER_ROW);
    },
    [_items]
  );

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-anavatan-grid-row-wrapper" ref={ref}>
        {chunkedItems.map((rowItems, rowIndex) => (
          <MovieRow
            key={"row_" + rowIndex}
            items={rowItems}
            onFocus={onFocus}
            navigate={navigate}
          />
        ))}
      </div>
    </FocusContext.Provider>
  );
};
